import { useEffect, useMemo, useRef, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { HelpBar } from "./salesShared";
import { Search, Eye, EyeOff, Plus, Trash2, Save, Boxes } from "lucide-react";

export default function ArtikelenTab({ cfg, reload }) {
  const [block, setBlock] = useState([]);
  const [custom, setCustom] = useState([]);
  const [q, setQ] = useState("");
  const [nn, setNn] = useState("");
  const [nnaam, setNnaam] = useState("");
  const [nsample, setNsample] = useState(false);
  const [nscode, setNscode] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => { setBlock(cfg.blocklist || []); setCustom((cfg.custom || []).map((c) => ({ ...c }))); }, [cfg]);

  const latest = useRef({ block: [], custom: [] });
  useEffect(() => { latest.current = { block, custom }; }, [block, custom]);
  const saveTimer = useRef(null);
  const persist = async ({ silent } = {}) => {
    if (!silent) setSaving(true);
    try { await api.put("/sales/artikel-config", { blocklist: latest.current.block, custom: latest.current.custom }); if (!silent) toast.success("Artikelen bijgewerkt"); reload && reload(); }
    catch (e) { toast.error(formatApiError(e)); } finally { if (!silent) setSaving(false); }
  };
  const scheduleSave = () => { clearTimeout(saveTimer.current); saveTimer.current = setTimeout(() => persist({ silent: true }), 700); };

  const blockSet = new Set(block.map(String));
  const toggle = (nr) => { setBlock((b) => (b.map(String).includes(String(nr)) ? b.filter((x) => String(x) !== String(nr)) : [...b, String(nr)])); scheduleSave(); };
  const addCustom = () => {
    if (!nnaam.trim()) return toast.error("Vul een artikelnaam in.");
    setCustom((c) => [...c, { artikelnummer: nn.trim(), naam: nnaam.trim(), sample: nsample, samplecode: nsample ? nscode.trim() : "" }]);
    setNn(""); setNnaam(""); setNsample(false); setNscode(""); scheduleSave();
  };
  const removeCustom = (i) => { setCustom((c) => c.filter((_, idx) => idx !== i)); scheduleSave(); };

  const save = () => persist();

  const all = cfg.all || [];
  const filtered = useMemo(() => {
    const s = q.trim().toLowerCase();
    return all.filter((a) => !s || `${a.artikelnummer} ${a.naam}`.toLowerCase().includes(s));
  }, [all, q]);

  const zichtbaar = all.length - blockSet.size + custom.length;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-bold text-slate-800 flex items-center gap-2"><Boxes className="h-5 w-5 text-blue-700" /> Artikelenbeheer</h2>
        <Button data-testid="artikel-save" onClick={save} disabled={saving} className="bg-[#1C64B0] hover:bg-[#154E8A]"><Save className="h-4 w-4 mr-1" /> {saving ? "Opslaan..." : "Opslaan"}</Button>
      </div>
      <HelpBar>Bepaal welke artikelen het salesteam kan kiezen in offertes/afspraken. <b>Verberg</b> bijvoorbeeld private labels met het oog-icoon. Je kunt ook <b>eigen artikelen</b> toevoegen die (nog) niet in de productie staan. Wijzigingen worden <b>automatisch opgeslagen</b>. Zichtbaar voor sales: <b>{zichtbaar}</b>.</HelpBar>

      <div className="rounded-lg border border-slate-200 bg-white p-4">
        <div className="text-sm font-bold text-slate-700 mb-2">Eigen sales-artikelen</div>
        <div className="flex flex-wrap items-end gap-2 mb-3">
          <div><label className="block text-xs font-semibold text-slate-600 mb-1">Artikelnr (optioneel)</label><Input data-testid="artikel-new-nr" value={nn} onChange={(e) => setNn(e.target.value)} className="w-40" /></div>
          <div><label className="block text-xs font-semibold text-slate-600 mb-1">Naam</label><Input data-testid="artikel-new-naam" value={nnaam} onChange={(e) => setNnaam(e.target.value)} className="w-64" placeholder="bijv. Kipfilethaasjes 2x2,5kg" /></div>
          <label className="flex items-center gap-2 pb-2 text-sm font-semibold text-amber-700 cursor-pointer select-none">
            <input type="checkbox" data-testid="artikel-new-sample" checked={nsample} onChange={(e) => setNsample(e.target.checked)} className="h-4 w-4 accent-amber-500" /> Sample
          </label>
          {nsample && <div><label className="block text-xs font-semibold text-slate-600 mb-1">Samplenummer</label><Input data-testid="artikel-new-samplecode" value={nscode} onChange={(e) => setNscode(e.target.value)} className="w-40" placeholder="bijv. S-2026-014" /></div>}
          <Button variant="outline" data-testid="artikel-add" onClick={addCustom}><Plus className="h-4 w-4 mr-1" /> Toevoegen</Button>
        </div>
        <div className="flex flex-wrap gap-2">
          {custom.length === 0 && <span className="text-sm text-slate-400">Nog geen eigen artikelen.</span>}
          {custom.map((c, i) => (
            <span key={i} data-testid="artikel-custom-chip" className={`inline-flex items-center gap-2 rounded-full border px-3 py-1 text-sm ${c.sample ? "border-amber-300 bg-amber-50 text-amber-800" : "border-emerald-200 bg-emerald-50 text-emerald-800"}`}>
              {c.sample && <span className="rounded bg-amber-500 px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wide text-white">Sample{c.samplecode ? ` ${c.samplecode}` : ""}</span>}
              {c.artikelnummer ? `${c.artikelnummer} - ` : ""}{c.naam}
              <button data-testid="artikel-custom-del" onClick={() => removeCustom(i)} className={c.sample ? "text-amber-500 hover:text-rose-600" : "text-emerald-500 hover:text-rose-600"}><Trash2 className="h-3.5 w-3.5" /></button>
            </span>
          ))}
        </div>
      </div>

      <div className="rounded-lg border border-slate-200 bg-white">
        <div className="p-3 border-b border-slate-100 flex items-center gap-2">
          <Search className="h-4 w-4 text-slate-400" />
          <input data-testid="artikel-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Zoek artikelnr of naam..." className="w-full text-sm outline-none" />
        </div>
        <div className="max-h-[420px] overflow-y-auto divide-y divide-slate-50">
          {filtered.length === 0 && <p className="p-4 text-sm text-slate-400">Geen artikelen gevonden.</p>}
          {filtered.map((a) => {
            const hidden = blockSet.has(String(a.artikelnummer));
            return (
              <div key={a.artikelnummer + a.naam} data-testid="artikel-row" className={`flex items-center justify-between px-3 py-2 text-sm ${hidden ? "bg-slate-50" : ""}`}>
                <div className={hidden ? "text-slate-400 line-through" : "text-slate-700"}><span className="font-mono text-xs text-slate-400 mr-2">{a.artikelnummer}</span>{a.naam}</div>
                <button data-testid="artikel-toggle" onClick={() => toggle(a.artikelnummer)} title={hidden ? "Weer tonen" : "Verbergen voor sales"} className={`inline-flex items-center gap-1 rounded-md px-2 py-1 text-xs font-semibold ${hidden ? "bg-slate-200 text-slate-600" : "bg-emerald-100 text-emerald-700"}`}>
                  {hidden ? <><EyeOff className="h-3.5 w-3.5" /> Verborgen</> : <><Eye className="h-3.5 w-3.5" /> Zichtbaar</>}
                </button>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
